import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Video, CheckCircle, Loader, Calendar, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookingSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');
  const [status, setStatus] = useState('checking');
  const [transaction, setTransaction] = useState(null);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setTransaction(response.data);
          setStatus('success');
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setStatus('success');
        }
      } catch (error) {
        setStatus('error');
      }
    };
    
    poll();
  };

  return (
    <Layout>
      <section className="py-16 min-h-screen flex items-center justify-center" data-testid="booking-success">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="cassette-card p-12 text-center">
            {status === 'checking' && (
              <>
                <Loader className="w-16 h-16 mx-auto mb-6 text-[#6B4423] animate-spin" />
                <h1 className="text-3xl retro-title text-[#3D2817] mb-4">Processing...</h1>
                <p className="text-[#6B4423]">Confirming your booking</p>
              </>
            )}
            
            {status === 'success' && (
              <>
                <Video className="w-20 h-20 mx-auto mb-6 text-[#6B4423]" />
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-700" />
                <h1 className="text-4xl retro-title text-[#3D2817] mb-4">Booking Confirmed!</h1>
                <p className="text-[#6B4423] text-lg mb-6">
                  Your 1-on-1 Zoom session has been scheduled.
                </p>
                
                <div className="bg-[#6B4423]/10 p-6 rounded mb-6 text-left space-y-3">
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-5 h-5 text-[#6B4423]" />
                    <span className="text-[#3D2817]">Booking confirmed</span>
                  </div>
                  {transaction && (
                    <div className="flex items-center space-x-3">
                      <Clock className="w-5 h-5 text-[#6B4423]" />
                      <span className="text-[#3D2817]">45 minutes</span>
                    </div>
                  )}
                  <p className="text-[#6B4423] text-sm mt-4">
                    📧 You'll receive an email with:
                  </p>
                  <ul className="text-[#6B4423] text-sm ml-6 space-y-1">
                    <li>• Zoom meeting link</li>
                    <li>• Calendar invitation</li>
                    <li>• Pre-session questionnaire</li>
                  </ul>
                </div>
                
                <Link to="/" className="retro-button inline-block" data-testid="return-home">
                  Return to Home
                </Link>
              </>
            )}
            
            {status === 'error' && (
              <>
                <h1 className="text-3xl retro-title text-[#3D2817] mb-4">Something Went Wrong</h1>
                <p className="text-[#6B4423] mb-6">
                  We couldn't verify your booking. Please check your email for confirmation.
                </p>
                <Link to="/" className="retro-button inline-block">
                  Return to Home
                </Link>
              </>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}